import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { z } from 'zod'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import useMutationUpdateGeneralSetting from '@/modules/client-setting/api/hooks/useMutationUpdateGeneralSetting'
import { GetGeneralSettingResponse } from '@/modules/client-setting/type'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import useHasPermission from '@/common/hooks/useHasPermission'
import MinimumWithdrawBox from '@/modules/manage-player/components/MinimumWithdraw/components/MinimumWithdrawBox'
import { WalletType } from '@/schema'
import { BankProviderSelector } from '@/common/components/BankProviderSelector'
import useQueryGetBankProvider from '@/modules/bank/api/hooks/useQueryGetBankProvider'

const formSchema = z.object({
	minimumDepositAmount: z
		.number({
			required_error: 'โปรดกรอกจำนวนฝากขั้นต่ำ',
			invalid_type_error: 'โปรดกรอกจำนวนฝากขั้นต่ำ',
		})
		.min(1),
	minimumWithdrawalAmount: z
		.number({
			required_error: 'โปรดกรอกจำนวนถอนขั้นต่ำ',
			invalid_type_error: 'โปรดกรอกจำนวนถอนขั้นต่ำ',
		})
		.min(1),
	minimumTransferAmount: z
		.number({
			required_error: 'โปรดกรอกจำนวนโอนขั้นต่ำ',
			invalid_type_error: 'โปรดกรอกจำนวนโอนขั้นต่ำ',
		})
		.min(0)
		.optional(),
	manualApprovalWithdrawThreshold: z
		.number({
			required_error: 'โปรดกรอกจำนวนถอนขั้นต่ำ',
			invalid_type_error: 'โปรดกรอกจำนวนถอนขั้นต่ำ',
		})
		.min(0),
	manualApprovalWithdrawMultiplier: z
		.number({
			invalid_type_error: 'โปรดกรอกตัวเลขที่ถูกต้อง',
		})
		.min(0, 'ค่าต้องไม่ติดลบ')
		.optional(),
	minimumEasyClearTurnover: z
		.number({
			required_error: 'โปรดกรอกจำนวนเงินขั้นต่ำ',
			invalid_type_error: 'โปรดกรอกจำนวนเงินขั้นต่ำ',
		})
		.min(0),
	quickDepositButtons: z
		.string()
		.optional()
		.refine(
			(val) => {
				if (!val || val.trim() === '') return true
				const numbers = val.split(',').map((n) => n.trim())
				return numbers.every((n) => !isNaN(Number(n)) && Number(n) > 0)
			},
			{
				message: 'โปรดกรอกตัวเลขที่มากกว่า 0 คั่นด้วยเครื่องหมายจุลภาค',
			},
		),
	hiddenBankProviderCodes: z.array(z.string()).optional().nullable(),
})

type FormValues = z.infer<typeof formSchema>

interface TransactionSettingBoxProps {
	data: GetGeneralSettingResponse
}

export default function TransactionSettingBox({
	data,
}: TransactionSettingBoxProps) {
	const { mutate: updateGeneralSetting } = useMutationUpdateGeneralSetting()
	const { data: bankProviders } = useQueryGetBankProvider()

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			minimumDepositAmount: data?.minimumDepositAmount ?? undefined,
			minimumWithdrawalAmount: data?.minimumWithdrawalAmount ?? undefined,
			minimumTransferAmount:
				(data as { minimumTransferAmount?: number })?.minimumTransferAmount ??
				undefined,
			manualApprovalWithdrawThreshold:
				data?.manualApprovalWithdrawThreshold ?? undefined,
			manualApprovalWithdrawMultiplier:
				data?.manualApprovalWithdrawMultiplier ?? undefined,
			minimumEasyClearTurnover: data?.minimumEasyClearTurnover ?? undefined,
			quickDepositButtons: data?.quickDepositButtons?.join(', ') ?? '',
			hiddenBankProviderCodes: data?.hiddenBankProviderCodes ?? null,
		},
	})

	const hasWritePermission = useHasPermission(PermissionKeys.WEB_SETTING)

	const handleSubmit = (values: FormValues) => {
		try {
			const payload = {
				...values,
				quickDepositButtons:
					values.quickDepositButtons && values.quickDepositButtons.trim()
						? values.quickDepositButtons
								.split(',')
								.map((n) => Number(n.trim()))
								.filter((n) => !isNaN(n) && n > 0)
						: [],
				hiddenBankProviderCodes: values.hiddenBankProviderCodes || undefined,
			}

			updateGeneralSetting(
				{ payload },
				{
					onSuccess: () => {
						toast.success('บันทึกการตั้งค่าเรียบร้อยแล้ว')
					},
					onError: (error) => {
						showErrorToast(error)
					},
				},
			)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	return (
		<div className="space-y-4 sm:space-y-6">
			<Card>
				<CardHeader className="pb-4 sm:pb-6">
					<CardTitle className="text-lg sm:text-xl">ตั้งค่าธุรกรรม</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4 sm:space-y-6">
					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(handleSubmit)}
							className="space-y-4 sm:space-y-6"
						>
							<FormField
								control={form.control}
								name="minimumDepositAmount"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											จำนวนฝากขั้นต่ำ
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												placeholder="กรอกจำนวนฝากขั้นต่ำ"
												{...field}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : null,
													)
												}
												disabled={!hasWritePermission}
												className="text-sm sm:text-base"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="minimumWithdrawalAmount"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											จำนวนถอนขั้นต่ำ
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												placeholder="กรอกจำนวนถอนขั้นต่ำ"
												{...field}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : null,
													)
												}
												disabled={!hasWritePermission}
												className="text-sm sm:text-base"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="minimumTransferAmount"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											จำนวนโอนขั้นต่ำ
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												placeholder="กรอกจำนวนโอนขั้นต่ำ"
												{...field}
												value={field.value ?? ''}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : undefined,
													)
												}
												disabled={!hasWritePermission}
												className="text-sm sm:text-base"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="manualApprovalWithdrawThreshold"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											จำนวนถอนขั้นต่ำที่ต้องมีการอนุมัติ
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												placeholder="กรอกจำนวนถอนขั้นต่ำที่ต้องมีการอนุมัติ"
												{...field}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : null,
													)
												}
												disabled={!hasWritePermission}
												className="text-sm sm:text-base"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="manualApprovalWithdrawMultiplier"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											จำนวนการถอนขั้นต่ำ ด้วยตัวคูณ (เช่น 2 = ถ้าฝากครั้งล่าสุด
											100 ถอนเกิน 200 ต้องอนุมัติ)
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												step="0.1"
												placeholder="กรอกตัวคูณ (เช่น 2 หรือ 1.5) หรือปล่อยว่างเพื่อปิดใช้งาน"
												{...field}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : null,
													)
												}
												disabled={!hasWritePermission}
												className="text-sm sm:text-base"
											/>
										</FormControl>
										<p className="text-xs text-muted-foreground sm:text-sm">
											ถ้าไม่กรอกหรือกรอก 0 จะปิดใช้งานฟีเจอร์นี้
										</p>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="minimumEasyClearTurnover"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											จำนวนเงินสูงสุดที่อนุญาตให้ฝากเงินเพิ่มแม้เงื่อนไข
											promotion ไม่สำเร็จ
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												placeholder="กรอกจำนวนเงินสูงสุด"
												{...field}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : null,
													)
												}
												disabled={!hasWritePermission}
												className="text-sm sm:text-base"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="quickDepositButtons"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											ปุ่มฝากเงินด่วน
										</FormLabel>
										<FormControl>
											<Input
												placeholder="ตัวอย่าง: 100, 500, 1000, 5000"
												{...field}
												disabled={!hasWritePermission}
												className="text-sm sm:text-base"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="hiddenBankProviderCodes"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-sm sm:text-base">
											ธนาคารที่ซ่อนไม่แสดงให้ผู้เล่น
										</FormLabel>
										<FormControl>
											<BankProviderSelector
												field={field}
												bankProviders={bankProviders}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							{hasWritePermission && (
								<div className="flex justify-center pt-2 sm:justify-end sm:pt-4">
									<Button type="submit" size="lg" className="w-full sm:w-auto">
										บันทึก
									</Button>
								</div>
							)}
						</form>
					</Form>
				</CardContent>
			</Card>

			{/* Minimum Withdraw Settings Card */}
			<Card>
				<CardContent className="space-y-4 sm:space-y-6">
					<div className="pt-3 text-base font-bold sm:pt-4 sm:text-lg">
						ตั้งค่าโอนขั้นต่ำ
					</div>
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
						<MinimumWithdrawBox walletType={WalletType.COMMISSION} />
						<MinimumWithdrawBox walletType={WalletType.DAILY_CHECK_IN} />
						<MinimumWithdrawBox walletType={WalletType.INVITE_FRIEND} />
						<MinimumWithdrawBox walletType={WalletType.PARTNER} />
						<MinimumWithdrawBox walletType={WalletType.RETURN_LOSS} />
						<MinimumWithdrawBox walletType={WalletType.WHEEL} />
					</div>
				</CardContent>
			</Card>
		</div>
	)
}
